// src/features/owner/components/KpiCard.tsx — KPI metric card with icon, value, and trend badge.
import styled from 'styled-components';
import { Icon, type IconName } from '@/icons';
import { ChangeBadge } from '../charts/ChangeBadge';
import type { KpiTrend } from '@/core/models';

const TONE_BG: Record<string, string> = {
  brand: 'rgba(99,102,241,.15)',
  blue: 'rgba(59,130,246,.15)',
  mint: 'rgba(16,185,129,.15)',
  ember: 'rgba(249,115,22,.15)',
  magenta: 'rgba(236,72,153,.15)',
  teal: 'rgba(20,184,166,.15)',
};

const Card = styled.div`
  background: ${({ theme }) => theme.color.glassBg};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.lg};
  padding: 14px;
`;

const IconWrap = styled.div<{ $tone: string }>`
  width: 34px;
  height: 34px;
  border-radius: 10px;
  display: grid;
  place-items: center;
  margin-bottom: 9px;
  background: ${({ $tone }) => TONE_BG[$tone] ?? TONE_BG.brand};
  color: ${({ theme }) => theme.color.brandFrom};
`;

const Label = styled.div`
  color: ${({ theme }) => theme.color.textMuted};
  font-size: ${({ theme }) => theme.fontSize.xs};
`;

const Value = styled.div`
  font-size: ${({ theme }) => theme.fontSize.xl};
  font-weight: 700;
  margin: 2px 0 5px;
  color: ${({ theme }) => theme.color.textPrimary};
`;

export interface KpiCardProps {
  icon: IconName;
  tone: string;
  label: string;
  value: number;
  deltaPct: number | null;
  trend: KpiTrend;
}

export function KpiCard({ icon, tone, label, value, deltaPct, trend }: KpiCardProps) {
  return (
    <Card>
      <IconWrap $tone={tone}>
        <Icon name={icon} size={18} />
      </IconWrap>
      <Label>{label}</Label>
      <Value>{value.toLocaleString()}</Value>
      <ChangeBadge deltaPct={deltaPct} trend={trend} />
    </Card>
  );
}
